import { useMemo } from "react";
import { ALL_COLUMNS } from "../../../../util/helper";
import { isRemovedJobDataKey } from "../../../../util/removedJobGuards";

export const useDynamicColumns = (historyData) => {
  const knownMolPropTitles = useMemo(() => {
    const map = new Map();
    ALL_COLUMNS.forEach((c) => {
      if (String(c.key).startsWith("w_") && c.title) map.set(c.key, c.title);
    });
    map.set("w_amw", "MW");
    return map;
  }, []);

  const dynamicColumnGroups = useMemo(() => {
    const glideMap = new Map();
    const phaseMap = new Map();
    const admeMap = new Map();
    const molPropMap = new Map();

    historyData.forEach((mol) => {
      if (Array.isArray(mol.glide)) {
        mol.glide.forEach((g) => {
          if (!g?.file) return;
          const key = `glide_${g.file}_${g.ph_threshold}`;
          if (!glideMap.has(key)) glideMap.set(key, { key, ...g, hasValue: true });
        });
      }

      if (Array.isArray(mol.phase)) {
        mol.phase.forEach((p) => {
          if (!p?.file) return;
          const key = `phase_${p.file}_${p.ph_threshold}`;
          if (!phaseMap.has(key)) phaseMap.set(key, { key, ...p, hasValue: true });
        });
      }

      Object.keys(mol || {}).forEach((k) => {
        if (!k || ["smile", "molecule_id", "reward", "glide", "phase"].includes(k)) return;
        if (isRemovedJobDataKey(k)) return;

        if (k.startsWith("w_")) {
          const hasVal = mol[k] !== null && mol[k] !== undefined && mol[k] !== "";
          const prev = molPropMap.get(k);
          molPropMap.set(k, {
            sourceKey: k,
            key: `molprops_${k}`,
            title: knownMolPropTitles.get(k) || k.replace(/^w_/, "").replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
            width: 90,
            hasValue: (prev?.hasValue || false) || hasVal,
          });
          return;
        }

        if ((typeof mol[k] === "object" && mol[k] !== null && !Array.isArray(mol[k])) || k.includes(".")) {
          const lastDot = k.lastIndexOf(".");
          const parent = k.slice(0, lastDot);
          const child = k.slice(lastDot + 1);
          const safeKey = `adme_${parent}_${child}`.replace(/[^a-zA-Z0-9_]/g, "_");
          const hasVal = mol[k]?.value !== null && mol[k]?.value !== undefined && mol[k]?.value !== "";
          const prev = admeMap.get(k);
          admeMap.set(k, { fullKey: k, key: safeKey, parent, child, hasValue: (prev?.hasValue || false) || hasVal });
        }
      });
    });

    return {
      dynamicGlideColumns: Array.from(glideMap.values()),
      dynamicPhaseColumns: Array.from(phaseMap.values()),
      dynamicAdmeColumns: Array.from(admeMap.values()).filter((x) => x.hasValue),
      dynamicMolProps: Array.from(molPropMap.values()).filter((x) => x.hasValue),
    };
  }, [historyData, knownMolPropTitles]);

  return dynamicColumnGroups;
};
